import { Button } from "@9amhealth/shared";
import styled from "@emotion/styled";
import clsx from "clsx";
import type { FC } from "react";
import React, { useEffect, useRef } from "react";

import {
  APP_BREAKPOINT,
  signupHeaderHeightDesktop
} from "src/constants/layout";
import StatusBarBloc from "src/hybrid/components/StatusBarBloc";
import { toast, useBloc } from "src/state/state";
import BlockingLoadingOverlayController from "../BlockingLoadingOverlay/BlockingLoadingOverlayController";

interface Props {
  title: string;
  message?: string;
  buttonText?: string;
  visible?: boolean;
  textCenter?: boolean;
  dangerBackground?: boolean;
  action?: () => Promise<void>;
}

const Wrapper = styled.div<{ dangerBackground?: boolean }>`
  background: ${({ dangerBackground }): string =>
    dangerBackground
      ? "var(--color-danger)"
      : "var(--color-goldenhour-orange)"};
  padding: var(--space-xs) calc(var(--space-xs) * 1.5);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: calc(var(--space-sm) * 1.5);
  z-index: 6;
  width: 100%;
  box-sizing: border-box;
  position: sticky;
  top: 0;
  margin: 0 auto;

  &.text-center {
    justify-content: center;
    text-align: center;
  }

  @media screen and (max-width: ${APP_BREAKPOINT - 1}px) {
    padding-top: calc(var(--safe-area-inset-top, 0px) + var(--space-xs));

    html.tab-nav & {
      top: var(--tab-nav-height, 0px);
      padding-top: calc(var(--safe-area-top, 0px) + var(--space-xs) + 8px);
    }
  }

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    border-radius: 0.5rem;
    justify-content: center;
    width: calc(100% - (var(--space-md) * 2));
    top: ${signupHeaderHeightDesktop}px;
  }
`;

const VerticalLine = styled.div`
  &::before {
    content: "";
    display: block;
    width: 0.5px;
    height: calc(var(--space-xs) * 1.5);
    background: var(--color-charcoal);
  }
`;

const Prompt: FC<Props> = ({
  title,
  message,
  action,
  visible,
  buttonText,
  textCenter,
  dangerBackground = false
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [, { resetThemeDefault }] = useBloc(StatusBarBloc);
  const color = dangerBackground ? "#ee968a" : "#ffdeb8";

  const handleActionPress = (): void => {
    BlockingLoadingOverlayController.startLoading({
      bg: "transparent"
    });
    if (action) {
      action()
        .catch(() => {
          toast.error("error_generic");
        })
        .finally(() => {
          BlockingLoadingOverlayController.endLoading();
        });
    }
  };

  useEffect(() => {
    if (!visible) {
      return;
    }

    StatusBarBloc.temporaryThemeColor = color;
    resetThemeDefault();
    return () => {
      StatusBarBloc.temporaryThemeColor = null;
      resetThemeDefault();
    };
  }, [visible]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!visible || !el) {
      return;
    }

    const update = () => {
      const height = el.offsetHeight;
      document.documentElement.style.setProperty(
        "--prompt-banner-bottom",
        `${height}px`
      );
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty(
        "--prompt-banner-bottom",
        "0px"
      );
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    <>
      <Wrapper
        ref={wrapperRef}
        dangerBackground={dangerBackground}
        className={clsx({ "text-center": textCenter }, "nine-prompt")}
      >
        <p className="as-little m0">
          <span className="as-strong">{title}</span>
          {message?.length && (
            <span style={{ wordBreak: "break-all" }}>{" " + message}</span>
          )}
        </p>

        {buttonText && (
          <>
            <VerticalLine />
            <Button
              hideArrow
              size="small"
              theme="charcoal"
              onPress={handleActionPress}
            >
              {buttonText}
            </Button>
          </>
        )}
      </Wrapper>
    </>
  );
};

export default Prompt;
